export interface SLProblem {
  icon: string;
  title: string;
  text: string;
}

export interface SLBenefit {
  icon: string;
  title: string;
  text: string;
}

export interface SLStep {
  number: string;
  title: string;
  text: string;
}

export interface SLFaqItem {
  q: string;
  a: string;
}

export interface ServiceLandingData {
  slug: string;
  serviceId: number;
  seo: { title: string; description: string; };
  og: {
    title: string;
    description: string;
    image: string;
    url: string;
  };
  icon: string;
  accent: string;
  accentFrom: string;
  accentTo: string;
  hero: {
    badge: string;
    headline: string;
    headlineAccent: string;
    subheadline: string;
    ctaPrimary: string;
    image: string;
  };
  problems: {
    heading: string;
    subheading: string;
    items: SLProblem[];
  };
  benefits: {
    heading: string;
    subheading: string;
    items: SLBenefit[];
  };
  process: {
    heading: string;
    steps: SLStep[];
  };
  faq: {
    heading: string;
    items: SLFaqItem[];
  };
  cta: {
    heading: string;
    subheading: string;
  };
}

export const serviceLandings: ServiceLandingData[] = [
  {
    slug: 'weby',
    serviceId: 1,
    seo: {
      title: 'Tvorba webových stránok | boostio',
      description: 'Web, ktorý pracuje za vás - 24 hodín denne. Rýchla tvorba moderných webových stránok so zameraním na výsledky.',
    },
    og: {
      title: 'Tvorba webových stránok, ktoré predávajú | Boostio',
      description: 'Profesionálny web, ktorý zaujme, presvedčí a konvertuje. Rýchla tvorba moderných webových stránok pre slovenské firmy - od dizajnu po spustenie.',
      image: 'https://readdy.ai/api/search-image?query=stunning professional website design displayed on modern laptop screen elegant minimal white desk workspace soft natural light web agency branding premium feel warm cream background clean composition&width=1200&height=630&seq=og-weby-01&orientation=landscape',
      url: 'https://boostio.sk/sluzby/weby',
    },
    icon: 'ri-code-s-slash-line',
    accent: 'text-orange-500',
    accentFrom: 'from-orange-500',
    accentTo: 'to-amber-500',
    hero: {
      badge: 'Webové stránky, ktoré predávajú',
      headline: 'Váš web pracuje za vás -',
      headlineAccent: 'každý deň, bez prestávky',
      subheadline: 'Prvý dojem rozhoduje o tom, či vám zákazník zavolá alebo odíde ku konkurencii. Vytvoríme web, ktorý zaujme, presvedčí a konvertuje.',
      ctaPrimary: 'Chcem nový web',
      image: 'https://readdy.ai/api/search-image?query=modern%20professional%20laptop%20with%20beautiful%20clean%20website%20design%20displayed%20on%20screen%20minimalist%20white%20desk%20home%20office%20setup%20natural%20window%20light%20premium%20web%20design%20agency%20concept%20warm%20tones%20professional%20photography&width=900&height=700&seq=sl-weby-hero-01&orientation=landscape',
    },
    problems: {
      heading: 'Poznáte tieto situácie?',
      subheading: 'Ak hoci jedna z nich platí pre vás, váš web vás stojí zákazníkov - každý deň.',
      items: [
        {
          icon: 'ri-search-eye-line',
          title: 'Zákazníci vás online nenájdu',
          text: 'Ak váš web nie je viditeľný na Googli, existujete len pre tých, ktorí vás už poznajú. Noví zákazníci jednoducho idú inam.',
        },
        {
          icon: 'ri-speed-line',
          title: 'Web odoháňa namiesto aby lákal',
          text: 'Pomalý, zastaraný alebo neprehľadný web pôsobí nedôveryhodne. Väčšina návštevníkov odíde skôr, ako si prečíta prvú vetu.',
        },
        {
          icon: 'ri-smartphone-line',
          title: 'Na mobile nevyzerá dobre',
          text: 'Viac ako polovica zákazníkov navštívi váš web na mobile. Ak tam nevyzerá dobre, stratili ste ich - bez šance na návrat.',
        },
      ],
    },
    benefits: {
      heading: 'Čo dostanete navyše',
      subheading: 'Nie len pekné stránky - ale nástroj, ktorý aktívne pomáha vášmu podnikaniu rásť.',
      items: [
        {
          icon: 'ri-search-2-line',
          title: 'Základ pre viditeľnosť na Googli',
          text: 'Každý web stavíme s technickými základmi SEO - správna štruktúra, rýchlosť a nastavenia, vďaka ktorým vás Google rád zobrazuje.',
        },
        {
          icon: 'ri-shield-check-line',
          title: 'Dôveryhodná prezentácia',
          text: 'Profesionálny dizajn hovorí za vás, aj keď ste na stretnutí. Zákazníci si v prvých sekundách vytvoria názor - uistite sa, že je pozitívny.',
        },
        {
          icon: 'ri-cursor-fill',
          title: 'Navrhnutý na akciu',
          text: 'Každý prvok - od textu po tlačidlo - je na stránke preto, aby zákazníka viedol smerom ku kontaktu s vami, nie od neho.',
        },
        {
          icon: 'ri-pencil-ruler-2-line',
          title: 'Aktualizujete sami, bez programátora',
          text: 'Po odovzdaní nepotrebujete nás na každú zmenu. Texty, fotky, novinky - to zvládnete sami cez jednoduché rozhranie.',
        },
      ],
    },
    process: {
      heading: 'Ako celé to prebehne',
      steps: [
        { number: '01', title: 'Konzultácia zadarmo', text: 'Zavoláme si alebo sa stretneme. Chceme pochopiť vaše podnikanie, zákazníkov a ciele - nie len technické parametre.' },
        { number: '02', title: 'Dizajn a obsah', text: 'Navrhujeme vizuál, ktorý odráža vašu značku. Pomôžeme aj s textami - jednoducho, zrozumiteľne, predajne.' },
        { number: '03', title: 'Vývoj a testovanie', text: 'Kódujeme rýchlo a starostlivo. Pred spustením testujeme na rôznych zariadeniach a prehliadačoch.' },
        { number: '04', title: 'Spustenie a podpora', text: 'Web ide live. Ste s nami aj po odovzdaní - hosting, bezpečnosť a úpravy podľa vašich potrieb.' },
      ],
    },
    faq: {
      heading: 'Otázky, ktoré dostávame najčastejšie',
      items: [
        { q: 'Ako dlho trvá vytvorenie webu?', a: 'Záleží na rozsahu, ale štandardný firemný web dokončíme do 5–10 pracovných dní od schválenia dizajnu. Väčšie projekty individuálne dohodíme.' },
        { q: 'Budem môcť web aktualizovať sám?', a: 'Áno. Väčšinu webov dodávame s jednoduchým administračným rozhraním, kde môžete meniť texty, fotky a obsah bez akýchkoľvek technických znalostí.' },
        { q: 'Čo ak sa mi výsledok nebude páčiť?', a: 'Dizajn vám ukážeme ešte pred vývojom. K spusteniu pristúpime až po vašom schválení - súčasťou sú iterácie a úpravy.' },
        { q: 'Potrebujem aj hosting a doménu?', a: 'Môžeme zabezpečiť všetko - doménu, hosting, SSL certifikát. Ak už niečo máte, radi to využijeme a nastavíme.' },
      ],
    },
    cta: {
      heading: 'Pripravení mať web, ktorý predáva?',
      subheading: 'Napíšte nám a spoločne zistíme, čo váš web potrebuje na to, aby pracoval naplno.',
    },
  },
  {
    slug: 'eshopy',
    serviceId: 2,
    seo: {
      title: 'Tvorba e-shopov | boostio',
      description: 'E-shop, ktorý predáva - aj keď nepracujete. Výkonné online obchody navrhnuté na maximálny obrat.',
    },
    og: {
      title: 'Tvorba e-shopov, ktoré predávajú | Boostio',
      description: 'Online obchod navrhnutý tak, aby zákazník skutočne nakúpil. Plynulý nákupný proces, dôveryhodný dizajn a prehľad nad objednávkami - 24/7.',
      image: 'https://readdy.ai/api/search-image?query=modern e-commerce shopping experience laptop smartphone showing beautiful online store product pages clean minimal aesthetic cream white background professional studio feel premium quality&width=1200&height=630&seq=og-eshopy-01&orientation=landscape',
      url: 'https://boostio.sk/sluzby/eshopy',
    },
    icon: 'ri-store-2-line',
    accent: 'text-emerald-500',
    accentFrom: 'from-emerald-500',
    accentTo: 'to-teal-500',
    hero: {
      badge: 'E-shopy navrhnuté na predaj',
      headline: 'Váš obchod predáva -',
      headlineAccent: 'aj keď nepracujete',
      subheadline: 'Online predaj neodpočíva. Váš e-shop môže prijímať objednávky 24/7, ale len ak je navrhnutý tak, aby zákazník skutočne nakúpil.',
      ctaPrimary: 'Chcem e-shop',
      image: 'https://readdy.ai/api/search-image?query=modern%20e-commerce%20online%20store%20interface%20displayed%20on%20laptop%20and%20smartphone%20premium%20product%20photography%20clean%20minimal%20aesthetic%20white%20background%20professional%20studio%20lighting%20shopping%20concept&width=900&height=700&seq=sl-eshopy-hero-01&orientation=landscape',
    },
    problems: {
      heading: 'Prečo ľudia nenakúpia?',
      subheading: 'Väčšina zákazníkov odíde bez nákupu - nie preto, že nechcú. Ale preto, že im to nič neuľahčilo.',
      items: [
        {
          icon: 'ri-shopping-cart-line',
          title: 'Košík sa opúšťa',
          text: 'Zákazník pridá produkt do košíka, ale nákup nedokončí. Zložitý proces, nejasné náklady na dopravu alebo povinná registrácia - každé takéto trenie stojí tržby.',
        },
        {
          icon: 'ri-eye-off-line',
          title: 'Produkty nevyzerajú atraktívne',
          text: 'Zlé fotky, mätúce popisky a neprehľadné kategórie - zákazník stráca chuť nakupovať. Vizuál predáva rovnako ako cena.',
        },
        {
          icon: 'ri-secure-payment-line',
          title: 'Zákazníci neveria platbe',
          text: 'Ak e-shop nevyzerá dôveryhodne alebo chýbajú jasné informácie o vrátení tovaru, zákazník zaplatí u niekoho, komu verí viac.',
        },
      ],
    },
    benefits: {
      heading: 'E-shop navrhnutý tak, aby zákazník nakúpil',
      subheading: 'Každé rozhodnutie pri jeho tvorbe smeruje k jednému cieľu - dokončenej objednávke.',
      items: [
        {
          icon: 'ri-route-line',
          title: 'Plynulá cesta k nákupu',
          text: 'Od prvého produktu po potvrdenie objednávky - navrhujeme proces tak, aby bol čo najjednoduchší a zákazník nezastavil na pol ceste.',
        },
        {
          icon: 'ri-global-line',
          title: 'Predaj bez hraníc',
          text: 'Váš e-shop je dostupný odkiaľkoľvek, kedykoľvek. Otvorte si trh bez geografických obmedzení.',
        },
        {
          icon: 'ri-bar-chart-box-line',
          title: 'Prehľad nad objednávkami',
          text: 'Zásoby, objednávky, zákazníci - všetko na jednom mieste. Viete, čo sa predáva, čo nie a kde je priestor na rast.',
        },
        {
          icon: 'ri-lock-2-line',
          title: 'Bezpečné platby, dôveryhodný dizajn',
          text: 'Overené platobné brány, SSL certifikát a dizajn, ktorý pôsobí seriózne - zákazník nakupuje bez obáv.',
        },
      ],
    },
    process: {
      heading: 'Ako spustíme váš obchod',
      steps: [
        { number: '01', title: 'Analýza a stratégia', text: 'Porozprávame sa o vašich produktoch, zákazníkoch a konkurencii. Na tomto základe postavíme e-shop, ktorý im hovorí presne to, čo chcú počuť.' },
        { number: '02', title: 'Dizajn a produkty', text: 'Navrhujeme vzhľad e-shopu a pomáhame s nastavením produktov, kategórií a popisov tak, aby predávali.' },
        { number: '03', title: 'Platby a doprava', text: 'Nastavíme platobné brány, možnosti dopravy a všetko, čo zákazník potrebuje na dokončenie objednávky bez problémov.' },
        { number: '04', title: 'Spustenie a rast', text: 'E-shop ide live. Sledujeme výkon a pomáhame optimalizovať - pretože spustenie je len začiatok.' },
      ],
    },
    faq: {
      heading: 'Otázky, ktoré dostávame najčastejšie',
      items: [
        { q: 'Aké platformy používate pre e-shopy?', a: 'Pracujeme so Shopify, WooCommerce a Next.js. Odporúčame platformu na základe vašich potrieb, počtu produktov a plánovaného rastu - nie len podľa toho, čo vieme.' },
        { q: 'Môžem e-shop spravovať sám?', a: 'Áno. Všetky platformy, s ktorými pracujeme, majú jednoduché administračné rozhranie. Pridávanie produktov, cien a zliav zvládnete bez technických znalostí.' },
        { q: 'Čo ak predávam veľa produktov?', a: 'Aj veľké katalógy vieme spracovať. Dohodíme sa na importe, štruktúre kategórií a filtrov tak, aby zákazník rýchlo našiel, čo hľadá.' },
        { q: 'Ako rieši e-shop vrátenie tovaru a reklamácie?', a: 'Pomôžeme vám nastaviť jasné podmienky a procesy. Zákazník, ktorý vie, že vrátenie je jednoduché, nakupuje s väčšou dôverou.' },
      ],
    },
    cta: {
      heading: 'Pripravení predávať online?',
      subheading: 'Opíšte nám váš produkt a zákazníka - spoločne nájdeme, ako z toho urobiť fungujúci e-shop.',
    },
  },
  {
    slug: 'marketing',
    serviceId: 3,
    seo: {
      title: 'Online marketing a reklama | boostio',
      description: 'PPC kampane a SEO stratégie, ktoré prinášajú merateľné výsledky. Každý cent vášho rozpočtu pracuje naplno.',
    },
    og: {
      title: 'Online marketing a reklama, ktorá prináša zákazníkov | Boostio',
      description: 'Koniec plytvania na reklamu, čo nikam nevedie. Riadime PPC kampane a SEO s merateľnými výsledkami - viete presne, čo ste zaplatili a čo ste za to dostali.',
      image: 'https://readdy.ai/api/search-image?query=professional digital marketing analytics dashboard on desktop monitor showing graphs charts performance metrics bright modern office desk clean workspace warm lighting minimal background premium feel&width=1200&height=630&seq=og-marketing-01&orientation=landscape',
      url: 'https://boostio.sk/sluzby/marketing',
    },
    icon: 'ri-line-chart-line',
    accent: 'text-amber-500',
    accentFrom: 'from-amber-500',
    accentTo: 'to-orange-500',
    hero: {
      badge: 'Reklama, za ktorú platíte iba keď funguje',
      headline: 'Koniec plytvania',
      headlineAccent: 'na reklamu, čo nikam nevedie',
      subheadline: 'Riadime kampane, ktoré oslovujú ľudí s reálnym záujmom o váš produkt - nie len každého v okolí. Viete presne, čo ste zaplatili a čo ste za to dostali.',
      ctaPrimary: 'Chcem efektívnu reklamu',
      image: 'https://readdy.ai/api/search-image?query=professional%20digital%20marketing%20workspace%20multiple%20screens%20showing%20analytics%20dashboard%20and%20advertising%20campaign%20performance%20charts%20clean%20modern%20desk%20focused%20lighting%20minimal%20background%20warm%20tones&width=900&height=700&seq=sl-marketing-hero-01&orientation=landscape',
    },
    problems: {
      heading: 'Znejú vám tieto problémy povedomne?',
      subheading: 'Väčšina firiem prichádza s rovnakými frustráciami. A všetky majú riešenie.',
      items: [
        {
          icon: 'ri-money-dollar-circle-line',
          title: 'Neviete, kde vaše peniaze idú',
          text: 'Reklama beží, ale koľko vás stojí jeden zákazník? Ak to neviete, nemôžete ani zlepšovať. Bez dát ste slepí.',
        },
        {
          icon: 'ri-bar-chart-line',
          title: 'Kampane bežia, predaj ale neroste',
          text: 'Kliknutia sú, dosah je - len zákazníci neprichádzajú. Problém zvyčajne nie je v reklamnom systéme, ale v nastavení, cielení alebo ponuke.',
        },
        {
          icon: 'ri-trophy-line',
          title: 'Konkurencia vás predbieha',
          text: 'Kým vy experimentujete, konkurencia má nastavené kampane, ktoré fungujú. Každý deň omeškania je príležitosť, ktorú stráca niekto iný.',
        },
      ],
    },
    benefits: {
      heading: 'Čo sa zmení, keď reklamu prevezmeme my',
      subheading: 'Nerobíme reklamu pre reklamu. Robíme ju preto, aby priniesla zákazníkov a obrat.',
      items: [
        {
          icon: 'ri-crosshair-2-line',
          title: 'Cielenie na tých, čo skutočne kupujú',
          text: 'Na základe dát nastavíme kampane tak, aby ste platili za ľudí s reálnym záujmom - nie za klikačov bez úmyslu nakúpiť.',
        },
        {
          icon: 'ri-eye-2-line',
          title: 'Vždy viete, čo sa deje',
          text: 'Pravidelné reporty v jasnom jazyku - nie technické tabuľky. Viete, čo funguje, čo nie a čo plánujeme ďalej.',
        },
        {
          icon: 'ri-refresh-line',
          title: 'Kontinuálna optimalizácia',
          text: 'Nespustíme kampaň a nezabudneme na ňu. Sledujeme výsledky a priebežne upravujeme - pretože trh sa mení a reklama musí reagovať.',
        },
        {
          icon: 'ri-rocket-line',
          title: 'Rast, ktorý môžete škálovať',
          text: 'Keď nájdeme to, čo funguje, vieme to zosilniť. Úspešné kampane sú základ, nie strop.',
        },
      ],
    },
    process: {
      heading: 'Ako to spolu rozbehujeme',
      steps: [
        { number: '01', title: 'Audit a ciele', text: 'Pozrieme sa, čo máte, čo nefunguje a prečo. Dohodíme realistické ciele a stratégiu, ktorá k nim vedie.' },
        { number: '02', title: 'Nastavenie kampaní', text: 'Vytvoríme alebo prestavíme kampane - texty, vizuály, cielenie, rozpočet. Všetko s jedným cieľom: výsledky.' },
        { number: '03', title: 'Spustenie a meranie', text: 'Kampane idú live. Od prvého dňa sledujeme čísla a zbierame dáta potrebné na optimalizáciu.' },
        { number: '04', title: 'Optimalizácia a reporting', text: 'Každý týždeň vyhodnocujeme, upravujeme a informujeme vás. Žiadne prekvapenia, žiadne výhovorky.' },
      ],
    },
    faq: {
      heading: 'Najčastejšie otázky',
      items: [
        { q: 'Aký minimálny rozpočet na reklamu odporúčate?', a: 'Záleží na odvetví a cieľoch. Vieme efektívne pracovať aj s menšími rozpočtami - dôležité je, aby sme ho nastavili rozumne a nie ho plytval na nesprávne miesta.' },
        { q: 'Kedy uvidíme prvé výsledky?', a: 'PPC kampane môžu priniesť prvé dáta do niekoľkých dní. SEO je dlhodobejší proces - prvé viditeľné výsledky prichádzajú zvyčajne po niekoľkých mesiacoch.' },
        { q: 'Spravujete aj existujúce kampane alebo len nové?', a: 'Oboje. Radi prevezme správu existujúcich kampaní, zhodnotíme stav a navrhneme, čo zlepšiť.' },
        { q: 'Čo ak nebudem spokojný s výsledkami?', a: 'Výsledky sú vždy predmetom otvorenej diskusie. Keď niečo nefunguje, povieme prečo a navrhneme riešenie - nie výhovorky.' },
      ],
    },
    cta: {
      heading: 'Zistíme, kde strácate zákazníkov',
      subheading: 'Napíšte nám - bezplatne sa pozrieme na vaše aktuálne kampane a povieme vám, kde je priestor na zlepšenie.',
    },
  },
  {
    slug: 'socialne-siete',
    serviceId: 4,
    seo: {
      title: 'Správa sociálnych sietí | boostio',
      description: 'Správa Facebooku, Instagramu a LinkedInu. Budujeme komunity a premieňame sledovateľov na zákazníkov.',
    },
    og: {
      title: 'Správa sociálnych sietí bez stresu | Boostio',
      description: 'Pravidelný obsah, komunita a predaj zo sociálnych sietí - bez toho, aby ste trávili hodiny pred prázdnou stránkou. Facebook, Instagram, LinkedIn.',
      image: 'https://readdy.ai/api/search-image?query=social media content creation flatlay smartphone with beautiful instagram feed photos tablets warm pastel tones cream background minimal aesthetic professional photography content creator workspace&width=1200&height=630&seq=og-social-01&orientation=landscape',
      url: 'https://boostio.sk/sluzby/socialne-siete',
    },
    icon: 'ri-instagram-line',
    accent: 'text-rose-500',
    accentFrom: 'from-rose-500',
    accentTo: 'to-pink-500',
    hero: {
      badge: 'Sociálne siete bez stresu',
      headline: 'Vaša značka na sociálnych sieťach -',
      headlineAccent: 'bez toho, aby ste sa o to starali',
      subheadline: 'Obsah, ktorý zaujme, posty, ktoré pribúdajú pravidelne, a komunita, ktorá vám dôveruje - to všetko bez toho, aby ste trávili hodiny pred prázdnou stránkou.',
      ctaPrimary: 'Chcem správu sociálnych sietí',
      image: 'https://readdy.ai/api/search-image?query=creative%20workspace%20social%20media%20content%20creation%20smartphone%20displaying%20instagram%20feed%20with%20beautiful%20curated%20posts%20colorful%20flatlay%20desk%20natural%20lighting%20professional%20content%20creator%20setup%20minimal%20background&width=900&height=700&seq=sl-social-hero-01&orientation=landscape',
    },
    problems: {
      heading: 'Čo vás na sociálnych sieťach brzdí?',
      subheading: 'Tieto problémy riešia väčšina firiem. A každý z nich stojí príležitosti.',
      items: [
        {
          icon: 'ri-time-line',
          title: 'Nemáte čas na pravidelný obsah',
          text: 'Sociálne siete fungujú len vtedy, keď sú aktívne. Ak zverejňujete nepravidelne, algoritmus vás prestane zobrazovať a sledovatelia strácajú záujem.',
        },
        {
          icon: 'ri-question-mark',
          title: 'Neviete, čo zverejniť',
          text: 'Nemôžete posielať len reklamné príspevky. Ale čo iné? Obsah, ktorý buduje dôveru a zároveň vedie k predaju, je umenie samo o sebe.',
        },
        {
          icon: 'ri-group-line',
          title: 'Máte sledovateľov, ale nie zákazníkov',
          text: 'Veľký dosah bez konverzie je len pocit. Cieľom sociálnych sietí nie je „likes" - je to ľudia, ktorí prídu k vám a nakúpia.',
        },
      ],
    },
    benefits: {
      heading: 'Čo sa zmení s nami',
      subheading: 'Správa sociálnych sietí, ktorá pracuje pre váš biznis - nie len pre počty followerov.',
      items: [
        {
          icon: 'ri-calendar-check-line',
          title: 'Pravidelná prítomnosť bez vašej práce',
          text: 'Pripravíme obsah, schválite ho, my ho zverejnime. Vaše profily žijú - vy sa venujete podniku.',
        },
        {
          icon: 'ri-heart-3-line',
          title: 'Obsah, ktorý buduje dôveru',
          text: 'Ľudia nakupujú od značiek, ktorým veria. Obsah, ktorý ich eduká, baví alebo inšpiruje, buduje vzťah oveľa účinnejšie ako každá reklama.',
        },
        {
          icon: 'ri-user-add-line',
          title: 'Sledovatelia sa menia na zákazníkov',
          text: 'Každý príspevok je navrhnutý s cieľom - niektoré budujú dosah, niektoré dôveru, niektoré vedú priamo k akcii. Kombinácia funguje.',
        },
        {
          icon: 'ri-pie-chart-2-line',
          title: 'Prehľad, čo funguje a čo nie',
          text: 'Pravidelné reporty - čo mal najväčší dosah, kde ľudia klikajú a ako rastie záujem o vašu značku v čase.',
        },
      ],
    },
    process: {
      heading: 'Ako to spolu rozbehujeme',
      steps: [
        { number: '01', title: 'Spoznávame vás', text: 'Chceme pochopiť vašu značku, zákazníkov a to, ako chcete byť vnímaní. Na tom postavíme celý obsah.' },
        { number: '02', title: 'Stratégia a obsahový plán', text: 'Navrhujeme typy obsahu, tón komunikácie a plán zverejňovania - všetko prispôsobené vašej značke.' },
        { number: '03', title: 'Tvorba a schvaľovanie', text: 'Pripravujeme texty a vizuály, vy schvaľujete. Žiadne prekvapenia - vždy viete, čo ideme zverejniť.' },
        { number: '04', title: 'Správa a optimalizácia', text: 'Zverejňujeme, sledujeme výsledky a prispôsobujeme obsah tomu, čo rezonuje s vaším publikom.' },
      ],
    },
    faq: {
      heading: 'Najčastejšie otázky',
      items: [
        { q: 'Ktoré siete spravujete?', a: 'Primárne Facebook, Instagram a LinkedIn. Na základe vašich zákazníkov odporúčame, na ktoré sa zamerať - nie na všetky naraz.' },
        { q: 'Koľkokrát týždenne zverejňujete?', a: 'Závisí od zvoleného plánu. Dôležitejšia ako frekvencia je konzistentnosť a kvalita - radšej menej, ale pravidelne a hodnotne.' },
        { q: 'Schvaľujem každý príspevok?', a: 'Áno, ak chcete. Pripravíme obsah vopred, vy ho skontrolujete a schválite. Alebo nám dáte voľnú ruku - záleží na vás.' },
        { q: 'Čo ak mám existujúce profily?', a: 'Radi prevezmeme správu existujúcich profilov. Zhodnotíme stav, navrhneme zmeny a pokračujeme od tam, kde ste skončili.' },
      ],
    },
    cta: {
      heading: 'Vaše sociálne siete môžu pracovať za vás',
      subheading: 'Napíšte nám - ukážeme vám, ako môžeme vašu prítomnosť online posunúť na ďalšiu úroveň.',
    },
  },
  {
    slug: 'sprava-systemov',
    serviceId: 5,
    seo: {
      title: 'Správa webových systémov | boostio',
      description: 'Spoľahlivý hosting, monitoring a správa webových systémov. Váš web beží 24/7 bez výpadkov.',
    },
    og: {
      title: 'Správa webových systémov bez výpadkov | Boostio',
      description: 'Monitoring 24/7, bezpečnostné zálohy a technická podpora. Kým vy podnikáte, my sa staráme o vašu infraštruktúru - bez výpadkov a prekvapení.',
      image: 'https://readdy.ai/api/search-image?query=modern clean server technology infrastructure concept glowing network nodes connected visualization dark minimal background with warm orange accent light premium digital agency feel professional&width=1200&height=630&seq=og-server-01&orientation=landscape',
      url: 'https://boostio.sk/sluzby/sprava-systemov',
    },
    icon: 'ri-server-line',
    accent: 'text-violet-500',
    accentFrom: 'from-violet-500',
    accentTo: 'to-indigo-500',
    hero: {
      badge: 'Technická správa bez starostí',
      headline: 'Kým vy podnikáte,',
      headlineAccent: 'my sa staráme o vašu infraštruktúru',
      subheadline: 'Výpadky webu, bezpečnostné hrozby a technické problémy stoja čas, peniaze a reputáciu. Postaráme sa o to, aby sa nič z toho nestalo.',
      ctaPrimary: 'Chcem správu systémov',
      image: 'https://readdy.ai/api/search-image?query=modern%20server%20room%20data%20center%20technology%20infrastructure%20visualization%20network%20cables%20clean%20organized%20professional%20lighting%20orange%20and%20white%20accent%20minimal%20technical%20aesthetic&width=900&height=700&seq=sl-server-hero-01&orientation=landscape',
    },
    problems: {
      heading: 'Problémy, ktoré stoja firmy peniaze',
      subheading: 'Technické problémy sú neviditeľné - kým nenastaný. A vtedy vždy prídu v tom najhoršom momente.',
      items: [
        {
          icon: 'ri-alert-line',
          title: 'Web padol práve pri dôležitej kampani',
          text: 'Spustili ste reklamu, ľudia klikajú - a váš web nejde. Každá minúta výpadku stojí zákazníkov, peniaze a dôveru.',
        },
        {
          icon: 'ri-virus-line',
          title: 'Bezpečnostné hrozby sú reálne',
          text: 'Zastaraný software, slabé heslá alebo chýbajúce zálohy - tieto veci sa nevšímajú, kým nezaútočia. A potom je škoda oveľa väčšia, ako by bola prevencia.',
        },
        {
          icon: 'ri-tools-line',
          title: 'Nikto sa nevenuje pravidelnej údržbe',
          text: 'Web bez pravidelnej starostlivosti starne - výkon klesá, zraniteľnosti pribúdajú. Prevencia je lacnejšia ako oprava.',
        },
      ],
    },
    benefits: {
      heading: 'Čo dostanete s naším dohľadom',
      subheading: 'Spokojný zákazník je ten, ktorý nemusí riešiť technické problémy - pretože my ich riešime za neho, skôr ako nastanú.',
      items: [
        {
          icon: 'ri-24-hours-line',
          title: 'Monitoring nonstop',
          text: 'Systémy sledujeme nepretržite. Ak niečo prestane fungovať, vieme o tom skôr ako vy - a začneme to riešiť okamžite.',
        },
        {
          icon: 'ri-shield-keyhole-line',
          title: 'Bezpečnostné zálohy',
          text: 'Pravidelné automatické zálohy znamenajú, že aj v najhoršom scenári nepríde o všetko. Obnova je otázkou minút, nie dní.',
        },
        {
          icon: 'ri-speed-up-line',
          title: 'Udržiavaný výkon',
          text: 'Pravidelné aktualizácie, optimalizácia databáz a čistenie - web zostáva rýchly a funkčný dlhodobo, nie len po spustení.',
        },
        {
          icon: 'ri-phone-line',
          title: 'Podpora keď ju potrebujete',
          text: 'Máte otázku alebo nastane problém? Sme tu. Nemusíte čakať na tikety alebo hľadať, kto za čo zodpovedá.',
        },
      ],
    },
    process: {
      heading: 'Ako preberieme správu',
      steps: [
        { number: '01', title: 'Audit systémov', text: 'Pozrieme sa na aktuálny stav - hosting, bezpečnosť, výkon, zálohy. Zistíme, čo funguje, čo nie a čo je riziko.' },
        { number: '02', title: 'Plán a nastavenie', text: 'Navrhneme konkrétne riešenia a nastavíme automatizovaný monitoring, zálohovanie a bezpečnostné protokoly.' },
        { number: '03', title: 'Prevzatie správy', text: 'Preberáme zodpovednosť za infraštruktúru. Vy sa môžete sústrediť na podnikanie - technická stránka je na nás.' },
        { number: '04', title: 'Pravidelný reporting', text: 'Každý mesiac dostanete prehľad - dostupnosť, incidenty, aktualizácie a ďalší plán.' },
      ],
    },
    faq: {
      heading: 'Najčastejšie otázky',
      items: [
        { q: 'Čo všetko je zahrnuté v správe?', a: 'Monitoring dostupnosti, pravidelné aktualizácie, zálohovanie, bezpečnostné kontroly a technická podpora. Konkrétny rozsah záleží na zvolenej forme spolupráce.' },
        { q: 'Čo ak máme web na vlastnom hostingu?', a: 'Vieme pracovať s väčšinou hostingových prostredí. Ak niečo neumožňuje správnu správu, otvorene vám povieme prečo a navrhneme alternatívu.' },
        { q: 'Ako rýchlo reagujete pri výpadku?', a: 'Monitorovanie funguje nepretržite. Pri kritickom výpadku začíname riešiť okamžite - bez čakania na pracovné hodiny.' },
        { q: 'Môžem si správu kedykoľvek ukončiť?', a: 'Áno. Nepracujeme na záväzných dlhodobých zmluvách. Naša spokojnosť závisí od toho, či ste spokojní vy.' },
      ],
    },
    cta: {
      heading: 'Nechajte techniku na nás',
      subheading: 'Napíšte nám - zdarma vám zhodnotíme aktuálny stav vašich systémov a navrhneme, čo zlepšiť.',
    },
  },
  {
    slug: 'crm',
    serviceId: 6,
    seo: {
      title: 'Interné systémy a CRM | boostio',
      description: 'Koniec excelu a chaosu. Systémy šité na mieru - CRM, automatizácia procesov a prepojenie nástrojov.',
    },
    og: {
      title: 'CRM a interné systémy šité na mieru | Boostio',
      description: 'Koniec excelu a stratených kontaktov. Systém na mieru zjednotí váš tím, automatizuje opakujúce sa procesy a dá vám prehľad nad celou firmou.',
      image: 'https://readdy.ai/api/search-image?query=business team collaboration modern office workspace CRM dashboard displayed on monitor data visualization clean organized desk professional productivity atmosphere warm natural lighting minimal background&width=1200&height=630&seq=og-crm-01&orientation=landscape',
      url: 'https://boostio.sk/sluzby/crm',
    },
    icon: 'ri-database-2-line',
    accent: 'text-teal-500',
    accentFrom: 'from-teal-500',
    accentTo: 'to-emerald-500',
    hero: {
      badge: 'Systémy, ktoré robia poriadok',
      headline: 'Koniec excelu a stratených kontaktov -',
      headlineAccent: 'váš tím pracuje ako jeden celok',
      subheadline: 'Keď každý rieši zákazníkov po svojom, niečo sa vždy stratí. Systém šitý na mieru váš tím zjednotí, procesy zautomatizuje a vy budete mať prehľad.',
      ctaPrimary: 'Chcem systém na mieru',
      image: 'https://readdy.ai/api/search-image?query=modern%20business%20team%20collaborating%20around%20office%20desk%20with%20laptops%20and%20monitors%20displaying%20CRM%20dashboard%20data%20visualization%20clean%20professional%20workspace%20focused%20productive%20atmosphere%20warm%20lighting&width=900&height=700&seq=sl-crm-hero-01&orientation=landscape',
    },
    problems: {
      heading: 'Problémy, ktoré rastú s firmou',
      subheading: 'Čím väčšia firma, tým drahší chaos. Tieto problémy sa nezriešia samy od seba.',
      items: [
        {
          icon: 'ri-user-unfollow-line',
          title: 'Zákazníci sa strácajú medzi tímom',
          text: 'Každý má "svojich" zákazníkov v exceli. Keď odíde zamestnanec, história komunikácie zmizne s ním. Firma platí dvakrát za to, čo by malo fungovať automaticky.',
        },
        {
          icon: 'ri-repeat-line',
          title: 'Manuálne opakujúce sa úlohy',
          text: 'Vystavovanie faktúr, posielanie potvrdzovacích emailov, aktualizácia stavov objednávok - to všetko môže fungovať automaticky. Vy to robíte ručne.',
        },
        {
          icon: 'ri-eye-close-line',
          title: 'Nikto nemá celkový obraz',
          text: 'Koľko aktívnych ponúk máte? Kde sú uvoľnené kapacity? Kto je zodpovedný za ktorý projekt? Bez systému sa na tieto otázky odpovedá odhadom.',
        },
      ],
    },
    benefits: {
      heading: 'Čo systém na mieru prinesie',
      subheading: 'Jedno miesto pre celú firmu - zákazníci, úlohy, komunikácia, dokumenty a prehľady.',
      items: [
        {
          icon: 'ri-folder-user-line',
          title: 'Všetko na jednom mieste',
          text: 'Každý zákazník, každá komunikácia, každá úloha - všetko zdieľané, prehľadné a dostupné pre celý tím kedykoľvek.',
        },
        {
          icon: 'ri-robot-line',
          title: 'Automatizácia bez programovania',
          text: 'Opakujúce sa kroky sa dejú samy - notifikácie, followupy, zmeny stavov, reporty. Váš tím robí to, čo pridáva hodnotu.',
        },
        {
          icon: 'ri-line-chart-fill',
          title: 'Prehľad pre rozhodovanie',
          text: 'Nie len pocit - reálne dáta. Koľko príležitostí, kde sa strácajú, ako dlho trvajú procesy. Základ pre správne rozhodnutia.',
        },
        {
          icon: 'ri-git-branch-line',
          title: 'Prepojenie vašich nástrojov',
          text: 'Email, fakturácia, sklad, web - systém ich prepojí tak, aby data tiekli automaticky a vy nemuseli kopírovať z okna do okna.',
        },
      ],
    },
    process: {
      heading: 'Ako systém vzniká',
      steps: [
        { number: '01', title: 'Analýza procesov', text: 'Chceme pochopiť, ako vaša firma funguje - od prvého kontaktu so zákazníkom po faktúru. Mapa procesov je základ dobrého systému.' },
        { number: '02', title: 'Návrh riešenia', text: 'Navrhneme systém presne podľa vašich potrieb - nie off-the-shelf riešenie, ale niečo, čo sedí na váš spôsob práce.' },
        { number: '03', title: 'Vývoj a testovanie', text: 'Vyvíjame iteratívne a priebežne ukazujeme, ako systém rastie. Tím ho spozná skôr, ako ho odovzdáme.' },
        { number: '04', title: 'Onboarding a podpora', text: 'Prevedieme tím cez systém, nastavíme prístupy a ostávame k dispozícii počas rozbieha.' },
      ],
    },
    faq: {
      heading: 'Najčastejšie otázky',
      items: [
        { q: 'Je lepšie hotové CRM alebo systém na mieru?', a: 'Záleží od vašich potrieb. Hotové riešenia (Pipedrive, HubSpot) sú rýchlejšie a lacnejšie na štart. Systém na mieru sa oplatí, ak máte špecifické procesy, ktoré hotové riešenia nezvládajú.' },
        { q: 'Môžeme zapojiť existujúce nástroje?', a: 'Väčšinou áno. Pracujeme s API integráciami - email, faktúry, e-shop, sklad. Presný rozsah závisí od nástrojov, ktoré používate.' },
        { q: 'Ako dlho trvá vývoj CRM systému?', a: 'Základná verzia fungujúceho CRM môže byť hotová za niekoľko týždňov. Komplexnejšie systémy trvajú dlhšie - ale závisí od rozsahu a priority funkcií.' },
        { q: 'Čo ak sa naše procesy zmenia?', a: 'Systém navrhujeme s myšlienkou na budúcnosť. Zmeny a rozširovanie sú súčasťou bežnej spolupráce - nie jednorazový projekt.' },
      ],
    },
    cta: {
      heading: 'Zautomatizujte to, čo dnes robíte ručne',
      subheading: 'Popíšte nám, kde v procesoch strácate čas alebo prehľad - spoločne nájdeme, ako to zlepšiť.',
    },
  },
];
